import React, { useState } from "react";
import { Box, TextField, Button, Typography, CircularProgress } from "@mui/material";

interface TwoFactorAuthProps {
  tempToken: string;
  onVerificationSuccess: (data: any) => void;
  onBack: () => void;
  isLoading?: boolean;
  error?: string;
}

const TwoFactorAuth: React.FC<TwoFactorAuthProps> = ({ tempToken, onVerificationSuccess, onBack, isLoading = false, error: apiError }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const displayError = apiError || error;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError("Please enter a 6-digit code");
      return;
    }

    // This will be called by the parent component
    onVerificationSuccess({ tempToken, code });
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setCode(value);
      setError("");
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        Two-Factor Authentication
      </Typography>

      <Typography variant="body2" sx={{ mb: 4, color: "text.secondary" }}>
        Enter the 6-digit code from your authenticator app or use a backup code.
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Authentication Code"
          variant="standard"
          value={code}
          onChange={handleCodeChange}
          placeholder="123456"
          error={!!displayError}
          helperText={displayError}
          inputProps={{
            maxLength: 6,
            style: { textAlign: "center", fontSize: "1.5rem", letterSpacing: "0.5rem" },
          }}
          fullWidth
          margin="normal"
          autoFocus
        />

        <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
          <Button onClick={onBack} variant="outlined" disabled={isLoading} sx={{ textTransform: "none" }}>
            Back
          </Button>

          <Button type="submit" variant="contained" disabled={isLoading || code.length !== 6} sx={{ flex: 1, textTransform: "none" }}>
            {isLoading ? <CircularProgress size={24} /> : "Verify"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default TwoFactorAuth;
